// src/utils/kindCopy.ts
//
// The words of the kind (plan 2026-10-26, D9). A report is a dashboard row
// with `kind === "report"`, shown by the same page; what the reader reads
// there says Report and Page, never Dashboard or Tab. Every user-visible
// string on the report path that names the kind or its parts comes from
// this table, keyed by kind, so the two vocabularies cannot drift apart
// one ternary at a time.
//
// The "dashboard" column is the wording that shipped before D9, byte for
// byte: the dashboard path must not change. Anything that is not "report"
// (a missing kind included) reads as a dashboard.
//
// Two forms:
//  - whole sentences (`copyListMessage`, `staleMessage`, ...) wherever
//    capitalization or grammar would break if only a noun were swapped;
//  - `say(text)` for static descriptive copy that names the kind as a plain
//    noun ("This dashboard shares no data sources."): it swaps the nouns
//    and keeps the case. Do NOT run it over text where "tab" is a browser
//    tab or "dashboard" is a different thing than the open row (the
//    account-wide "every dashboard in your organization" lines).
//
// Not here: the Reports list (its own page), dashboard-only components
// (the tab bar, the dashboards overview, "Create Dashboard"), and every
// string a user does not read (storage keys, events, `?tab`, test ids,
// `surface`/`origin` enums, log lines).
import type { DashboardKind } from "@/api/dashboards";

export type CopyKind = DashboardKind;

export interface KindCopy {
  kind: CopyKind;
  /** dashboard / report */
  thing: string;
  /** Dashboard / Report */
  Thing: string;
  /** dashboards / reports */
  things: string;
  /** tab / page */
  part: string;
  /** Tab / Page */
  Part: string;
  /** tabs / pages */
  parts: string;
  /** Swap the nouns in static copy that names the open row or its parts. */
  say: (text: string) => string;

  editTitle: string;
  shareTitle: string;
  loadFailedTitle: string;
  changeFailedTitle: string;
  lastBuildLabel: string;
  noneOpen: string;
  createPartFailedTitle: string;

  copiedTitle: string;
  copyFailedTitle: string;
  copyDraftMessage: (name: string) => string;
  copyListMessage: (name: string) => string;


  staleTitle: string;
  staleMessage: string;
  unpublishTitle: string;
  unpublishedMessage: string;
  approvedMessage: string;
  returnedMessage: string;
  approveQuestion: string;
  approveOwn: string;
  returnQuestion: string;
  sendTo: string;
  publishQuestion: string;
  moreActionsLabel: string;

  imageCopyFailed: string;
  /** Copy-as-image refusals by capture kind; empty: the capture's own message. */
  imageErrors: Partial<Record<"too_tall" | "tab_changed" | "still_loading" | "render_failed", string>>;
  imageCopied: string;
  imageDownloaded: string;

  emptyGrid: string;
  settingUpTitle: string;
  dataThrough: string;
  /** The picker footer when the period follows the one period shared by
   *  every dashboard AND report of this person in this account. */
  periodInheriting: string;
  periodFollowAll: string;
}

function swapNouns(text: string, to: { thing: string; things: string; part: string; parts: string }): string {
  const keepCase = (from: string, word: string) =>
    from[0] === from[0].toUpperCase() ? word[0].toUpperCase() + word.slice(1) : word;
  return text
    .replace(/\b[Dd]ashboards\b/g, (m) => keepCase(m, to.things))
    .replace(/\b[Dd]ashboard\b/g, (m) => keepCase(m, to.thing))
    .replace(/\b[Tt]abs\b/g, (m) => keepCase(m, to.parts))
    .replace(/\b[Tt]ab\b/g, (m) => keepCase(m, to.part));
}

const DASHBOARD: KindCopy = {
  kind: "dashboard",
  thing: "dashboard",
  Thing: "Dashboard",
  things: "dashboards",
  part: "tab",
  Part: "Tab",
  parts: "tabs",
  say: (text) => text,

  editTitle: "Edit dashboard",
  shareTitle: "Share dashboard",
  loadFailedTitle: "Failed to load dashboard",
  changeFailedTitle: "Could not change the dashboard",
  lastBuildLabel: "Last dashboard build",
  noneOpen: "No dashboard is open.",
  createPartFailedTitle: "Could not create the tab",

  copiedTitle: "Dashboard copied",
  copyFailedTitle: "Could not copy dashboard",
  copyDraftMessage: (name) => `"${name}" is now your draft of the published dashboard.`,
  copyListMessage: (name) => `"${name}" is now in Your dashboards.`,


  staleTitle: "Dashboard changed",
  staleMessage: "This dashboard changed since you opened it. Reload to see the latest version.",
  unpublishTitle: "Unpublish dashboard",
  unpublishedMessage: "Dashboard unpublished.",
  approvedMessage: "Dashboard approved and published.",
  returnedMessage: "Dashboard returned to its author.",
  approveQuestion: "Approve this dashboard? It is published to",
  approveOwn: "You are approving your own dashboard.",
  returnQuestion: "Return this dashboard to its author? Give a reason so they know what to change.",
  sendTo: "Send this dashboard to",
  publishQuestion: "Publish the draft? Viewers of this dashboard see this version from now on.",
  moreActionsLabel: "More dashboard actions",

  imageCopyFailed: "The tab could not be copied as an image",
  imageErrors: {},
  imageCopied: "Copied the tab as an image",
  imageDownloaded: "Downloaded the tab as an image (your browser does not allow copying images)",

  emptyGrid: "No blocks have been added to this dashboard",
  settingUpTitle: "Setting up your dashboard",
  dataThrough: "Dashboard data through",
  periodInheriting: "Same period as your other dashboards.",
  periodFollowAll: "Use the period of all dashboards",
};

const REPORT: KindCopy = {
  kind: "report",
  thing: "report",
  Thing: "Report",
  things: "reports",
  part: "page",
  Part: "Page",
  parts: "pages",
  say: (text) => swapNouns(text, { thing: "report", things: "reports", part: "page", parts: "pages" }),

  editTitle: "Edit report",
  shareTitle: "Share report",
  loadFailedTitle: "Failed to load report",
  changeFailedTitle: "Could not change the report",
  lastBuildLabel: "Last report build",
  noneOpen: "No report is open.",
  createPartFailedTitle: "Could not create the page",

  copiedTitle: "Report copied",
  copyFailedTitle: "Could not copy report",
  copyDraftMessage: (name) => `"${name}" is now your draft of the published report.`,
  copyListMessage: (name) => `"${name}" is now in Your reports.`,


  staleTitle: "Report changed",
  staleMessage: "This report changed since you opened it. Reload to see the latest version.",
  unpublishTitle: "Unpublish report",
  unpublishedMessage: "Report unpublished.",
  approvedMessage: "Report approved and published.",
  returnedMessage: "Report returned to its author.",
  approveQuestion: "Approve this report? It is published to",
  approveOwn: "You are approving your own report.",
  returnQuestion: "Return this report to its author? Give a reason so they know what to change.",
  sendTo: "Send this report to",
  publishQuestion: "Publish the draft? Viewers of this report see this version from now on.",
  moreActionsLabel: "More report actions",

  imageCopyFailed: "The page could not be copied as an image",
  imageErrors: {
    too_tall: "This page is too tall to copy as one image",
    tab_changed: "The page changed while copying",
    still_loading: "The page is still loading its data. Copy it again when the numbers are in",
    render_failed: "The page could not be copied as an image",
  },
  imageCopied: "Copied the page as an image",
  imageDownloaded: "Downloaded the page as an image (your browser does not allow copying images)",

  emptyGrid: "No blocks have been added to this report",
  settingUpTitle: "Setting up your report",
  dataThrough: "Report data through",
  periodInheriting: "Same period as your dashboards and other reports.",
  periodFollowAll: "Use the period of all dashboards and reports",
};

const TABLE: Record<CopyKind, KindCopy> = { dashboard: DASHBOARD, report: REPORT };

/** The copy for a kind. Anything that is not "report" reads as a dashboard. */
export function kindCopy(kind: DashboardKind | null | undefined): KindCopy {
  return kind === "report" ? REPORT : DASHBOARD;
}
